import { useMe } from "@@/authentication/me/me-context";
import { useProvidersForUser } from "@@/providers/state/queries/providers-for-user/use-providers-for-user";
import { HorizontalDivider, HorizontalLine } from "@@/shared/dividers";
import { FlexRow } from "@@/shared/flex-containers";
import { ForEach } from "@@/shared/for-each";
import { Icon } from "@@/shared/icons/icon";
import { TextBox, TextEdit } from "@@/shared/text";
import { useTranslate } from "@@/translations/use-translate";
import { css, useTheme } from "@emotion/react";
import { faUsersCog } from "@fortawesome/pro-solid-svg-icons";
import { sortBy, translation } from "@towni/common";
import * as React from "react";
import { useEffect, useMemo, useRef } from "react";
import { useNavigate } from "react-router";

type Props = {
    divider?: React.JSX.Element;
};

const UserModalProviderLinks = (props: Props) => {
    const [me] = useMe();
    const translate = useTranslate();
    const theme = useTheme();
    const navigate = useNavigate();
    const inputRef = React.useRef<HTMLInputElement>(null);

    const [providersForUser] = useProvidersForUser(me?._id);
    const [filter, setFilter] = React.useState("");
    const providersSorted = useMemo(() => {
        return providersForUser
            .filter(item => {
                if (filter === "") return true;
                return translate(item.meta.title)
                    .toLowerCase()
                    .includes(filter);
            })
            .sort(sortBy(item => translate(item.meta.title)));
    }, [providersForUser, filter, translate]);

    // useEffect(() => {
    //     if (inputRef.current) {
    //         // Make it readonly initially
    //         inputRef.current.setAttribute("readonly", "readonly");

    //         // Remove readonly after a short delay
    //         setTimeout(() => {
    //             inputRef.current?.removeAttribute("readonly");
    //         }, 100);
    //     }
    // }, []);

    if (!providersForUser?.length) {
        return null;
    }

    return (
        <>
            <FlexRow fillParentWidth>
                <SafariAutofillBlocker inputRef={inputRef} className="w-full">
                    <TextEdit
                        autoFocus
                        type="search"
                        onEnter={() => {
                            if (providersSorted.length > 0) {
                                void navigate(
                                    `/bo/providers/${providersSorted[0].slug}`,
                                );
                            }
                            return;
                        }}
                        // Trying to prevent auto fill in safari >>
                        autoComplete="off"
                        autoCapitalize="off"
                        autoCorrect="off"
                        spellCheck="false"
                        data-form-type="other"
                        aria-autocomplete="none"
                        name="search-provider"
                        results={0}
                        // Trying to prevent auto fill in safari <<
                        placeholder={translation({
                            sv: "filtrera",
                            en: "filter",
                        })}
                        description={translation({
                            sv: "filtrera leverantörer på titel",
                            en: "filter providers by title",
                        })}
                        css={{
                            width: "100%",
                            padding: "10px 15px",
                        }}
                        value={filter}
                        onChange={value => {
                            setFilter(value.toLowerCase());
                        }}
                    />
                </SafariAutofillBlocker>
            </FlexRow>
            <ForEach itemOf={providersSorted} getKey={provider => provider._id}>
                {provider => (
                    <React.Fragment key={provider._id}>
                        <FlexRow
                            fillParentWidth
                            padding={{ topBottom: 20 }}
                            shrink={0}
                            mainAxis="center"
                            crossAxis="center"
                            onClick={() =>
                                navigate(`/bo/providers/${provider.slug}`)
                            }
                            background={{
                                color: theme.isDarkTheme
                                    ? theme.colors.tertiary.dark
                                    : theme.colors.tertiary.light,
                            }}>
                            <Icon
                                icon={faUsersCog}
                                color={theme.colors.black}
                            />
                            <HorizontalDivider />
                            <TextBox
                                text={provider.meta.title}
                                weight="700"
                                size="ML"
                                color={theme.colors.black}
                            />
                        </FlexRow>
                        {props.divider ?? <HorizontalLine fillParentWidth />}
                    </React.Fragment>
                )}
            </ForEach>
        </>
    );
};

export { UserModalProviderLinks };

/**
 * Component that wraps input elements to prevent Safari autofill suggestions
 * using multiple aggressive techniques.
 */
const SafariAutofillBlocker: React.FC<{
    children: React.ReactNode;
    inputRef?: React.RefObject<HTMLInputElement | null>;
    className?: string;
}> = ({ children, inputRef, ...props }) => {
    const formRef = useRef<HTMLFormElement>(null);
    const localInputRef = useRef<HTMLInputElement | null>(null);

    // Get the actual input element, either from props or by finding it in children
    useEffect(() => {
        if (!inputRef?.current) {
            const input = formRef.current?.querySelector("input");
            if (input) {
                localInputRef.current = input;
            }
        }
    }, [inputRef]);

    const targetInput = inputRef?.current || localInputRef.current;

    // Apply multiple techniques to prevent Safari autofill
    useEffect(() => {
        if (!targetInput) return;

        // Save original attributes to restore later
        const originalType = targetInput.getAttribute("type");
        const originalName = targetInput.getAttribute("name");
        const originalId = targetInput.getAttribute("id");

        // 1. Temporarily modify attributes to confuse Safari
        targetInput.setAttribute("type", "text");
        targetInput.setAttribute("name", `prevent-autofill-${Date.now()}`);
        if (originalId) {
            targetInput.setAttribute("id", `${originalId}-modified`);
        }

        // 2. Apply the readonly technique
        targetInput.setAttribute("readonly", "readonly");

        // 3. Reset to original values after Safari's autofill detection
        const timer = setTimeout(() => {
            if (originalType) targetInput.setAttribute("type", originalType);
            if (originalName) targetInput.setAttribute("name", originalName);
            if (originalId) targetInput.setAttribute("id", originalId);
            targetInput.removeAttribute("readonly");
        }, 200);

        return () => {
            clearTimeout(timer);
        };
    }, [targetInput]);

    // CSS to override Safari's autofill styling
    const autofillBlockerStyles = css`
        /* Hide Safari autofill background and text color */
        input:-webkit-autofill,
        input:-webkit-autofill:hover,
        input:-webkit-autofill:focus {
            -webkit-box-shadow: 0 0 0 1000px transparent inset !important;
            -webkit-text-fill-color: currentColor !important;
            transition: background-color 5000s ease-in-out 0s;
        }
    `;

    return (
        <form
            ref={formRef}
            autoComplete="off"
            css={autofillBlockerStyles}
            className={props.className}
            onSubmit={e => e.preventDefault()} // Prevent form submission
        >
            {/* Hidden dummy fields to catch autocomplete attempts */}
            <input
                type="text"
                name="prevent_email"
                style={{ display: "none" }}
            />
            <input
                type="password"
                name="prevent_password"
                style={{ display: "none" }}
            />
            <input
                type="email"
                name="prevent_email_alt"
                style={{ display: "none" }}
            />

            {children}
        </form>
    );
};

export { SafariAutofillBlocker };
